import React, { useEffect, useRef, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import { Button } from "antd";
import { HomeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { ActionType, RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { getSorterParams } from "../../../utils";
import {
  PhotoTagGetParams,
  PhotoTagTableRow,
  usePhotoTagTableGet,
} from "../../../hooks/api/photoTags";
import { CreateTagModal } from "./Create";
import DeleteTagButton from "./actions";

const columns = (t: TFunction): ProColumns<PhotoTagTableRow>[] => [
  {
    title: "Tag name",
    dataIndex: "name",
    key: "name",
    sorter: true,
    hideInSearch: true,
  },
];

interface ToolBarProps {
  onAddClick: () => void;
  t: TFunction;
}

function ToolBar({ onAddClick, t }: ToolBarProps): React.ReactElement[] {
  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onAddClick();
      }}
    >
      {t("common.table.add")}
    </Button>,
  ];
}

export default function PhotoTagsContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const photoTagTableGet = usePhotoTagTableGet();
  const actionRef = useRef<ActionType>(null);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Photo Tags" },
    ]);
  }, [i18n.language, t]);

  const handleAddClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCreateSuccess = () => {
    actionRef.current?.reload();
  };

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<PhotoTagTableRow>>> => {
    const newParams: PhotoTagGetParams = {
      page: current || 1,
      pageSize: pageSize || 20,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return photoTagTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <>
      <Table<PhotoTagTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle="List of Photo Tags"
        toolBarRender={() => ToolBar({ onAddClick: handleAddClick, t })}
        request={tableRequest}
        columns={columns(t)}
        actionRef={actionRef}
        actions={[
          [
            DeleteTagButton,
            {
              setTableKey,
            },
          ],
        ]}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        search={{
          style: {
            display: "none",
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search",
            style: { width: 296 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-photo-tags",
          persistenceType: "localStorage",
        }}
      />
      <CreateTagModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleCreateSuccess}
        t={t}
      />
    </>
  );
}
